import type { ProductsHeader } from '@/types/treeViewTypes'
import type { Language } from '~/libs/services/src/i18n'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router'
import { getProductsHeader } from '@/api'
import { useCountriesContext } from '@/contexts/countries/useCountriesContext'
import { getFacetFiltersFromSearchParams } from '@/contexts/products/facetFilters'
import { queryKeys } from '@/query/queryKeys'

export function useProductsHeader() {
  const params = useParams()
  const [searchParams] = useSearchParams()
  const { countries } = useCountriesContext()
  const { t, i18n } = useTranslation()

  const season = params.season as string
  const universeId = params.universeId as string
  const departmentId = params.departmentId as string
  const selectedSubdepartmentIds = searchParams.getAll('subdepartments').map(Number).filter(Number.isFinite)
  let subdepartmentIds: number[] | undefined
  if (selectedSubdepartmentIds.length > 0) {
    subdepartmentIds = selectedSubdepartmentIds
  }
  else if (params.subdepartmentId) {
    subdepartmentIds = [Number(params.subdepartmentId)]
  }

  const familyIds = searchParams.getAll('families')
  const productCode = searchParams.get('product_code')
  const filters = getFacetFiltersFromSearchParams(searchParams, countries)

  const currentLanguage = i18n.language.split('-')[0] as Language
  const isFullSubDep = location.pathname.includes('/full/')
  const isFullDepartment = location.pathname.includes('/all-families')

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.productsHeader(universeId, departmentId, familyIds, subdepartmentIds, season, filters, productCode),
    queryFn: async ({ signal }) => {
      if (!signal?.aborted) {
        return await getProductsHeader<ProductsHeader>({
          season,
          universeId: Number(universeId),
          departmentId: Number(departmentId),
          families: familyIds.map(Number),
          subdepartments: subdepartmentIds,
          filters,
          productCode,
        })
      }
    },
  })

  const { contextId, prefix, label } = useMemo(() => {
    if (isFullDepartment) {
      return { contextId: 'title_full-dep', prefix: t('products-page.department'), label: data?.department.labels[currentLanguage] }
    }

    if (isFullSubDep) {
      return { contextId: 'title_full-subdep', prefix: t('products-page.subdepartment'), label: data?.subdepartments[0].labels[currentLanguage] }
    }

    if (familyIds && familyIds.length > 1) {
      return { contextId: 'title_multiple-subdep', prefix: null, label: t('products-page.multiple-families') }
    }

    const family = data?.subdepartments[0].families[0]
    return { contextId: 'title_single-family', prefix: family?.id, label: family?.labels[currentLanguage] }
  // eslint-disable-next-line react/exhaustive-deps
  }, [isFullDepartment, isFullSubDep, data, currentLanguage])

  return {
    data,
    isLoading,
    contextId,
    prefix,
    label,
  }
}
